// Single source of truth for every project shown on the site.
// Order here is the order on the home grid. Images live in
// public/images/{slug}/{slug}.webp (thumbnail) and {slug}0-2.webp (slides).
// Each decision is rendered as two paragraphs: the problem, then what was
// done and how it turned out.

const projects = [
  {
    slug: 'purelog',
    title: 'PureLog',
    tagline: 'A workout log that suggests exercises from your own history',
    kind: 'Personal project',
    role: 'Solo: product design, full-stack development, and deployment',
    timeline: 'Sep 2026 - Present',
    stack: ['nextjs', 'react', 'javascript', 'supabase', 'postgresql', 'stripe', 'tailwind', 'vercel'],
    badges: ['NEXTJS', 'SUPABASE', 'POSTGRESQL', 'STRIPE'],
    links: {
      website: 'https://purelog.net/',
      // Private repository: code excerpts below are copied from it.
      github: null,
    },
    captions: ['Landing page', 'Logging a session with a next-exercise suggestion', 'Progress chart for one exercise'],
    overview: [
      "PureLog is a minimalist workout log for lifters who already know what they want to do. Users don't build or maintain routines. The app works from what they actually logged: it suggests a first exercise, prefills the last weights and reps, and offers the exercise they usually do next.",
      'I designed and built it myself, from the product spec and data model to Stripe billing and deployment on Vercel. It is live with a free tier and a $2.99/month Pro plan.',
    ],
    highlights: [
      'Google sign-in through Supabase Auth, with row-level security on every table that checks user_id on reads and writes.',
      'Server actions re-check the session and normalize every input. Exercise names have whitespace collapsed, a 50-character limit, and case-insensitive uniqueness backed by a unique index; dates are range-checked; and only sets the user marked complete are saved.',
      'First-exercise and next-exercise suggestions from recent sessions, shown only when the history has enough evidence for them.',
      'Stripe Checkout, Customer Portal, and signature-verified webhooks. The server enforces the free and Pro limits on history range and CSV export.',
      'The exercise in progress is saved as a localStorage draft and restored after a refresh. A daily Vercel Cron job archives exercises unused for 60 days, and adding one again restores it.',
    ],
    decisions: [
      {
        title: 'Closing a self-upgrade hole in row-level security',
        problem:
          'The first schema let users update their own profiles row. Because plan_tier lives on that row, any signed-in user could call profiles.update({ plan_tier: "pro" }) with the public anon key and get Pro for free. The update policies also had USING without WITH CHECK, so a user could change user_id on one of their rows and hand it to another account.',
        approach:
          'I made profiles read-only for clients. Only the Stripe webhook, running with the service role, and a security-definer signup trigger can write to it. I also re-created every update policy with a matching WITH CHECK, so Postgres validates the row as written as well as the row being replaced. Plan state now changes only through a verified Stripe event, and rows can\'t change owners.',
        file: 'supabase/migrations/0007 & 0008',
        language: 'sql',
        code: `-- 0007_profiles_read_only_for_users.sql
drop policy if exists "profiles_update_own" on public.profiles;
revoke insert, update, delete on public.profiles from authenticated, anon;

-- 0008_write_policies_with_check.sql
drop policy if exists "exercise_logs_update_own" on public.exercise_logs;
create policy "exercise_logs_update_own"
  on public.exercise_logs for update
  using (auth.uid() = user_id)
  with check (auth.uid() = user_id);`,
      },
      {
        title: 'Recommendations that need evidence',
        problem:
          'A suggestion like "what you usually do next" helps only when it\'s right. If the app always named the most frequent follow-up, a single coincidence would show up as a confident suggestion.',
        approach:
          'The logic lives in pure functions with no database access. It looks at the last five sessions that include the current exercise and collects what was logged right after it, skipping anything already done today. A candidate needs at least two occurrences, and ties go to the more recent one. Without enough history, the app shows only the search box. When it does suggest something, it shows the evidence ("Followed this 3 of the last 5 times").',
        file: 'lib/recommendation.js',
        code: `export function nextExerciseAfter(
  targetExerciseId,
  sessionsDayLogs,
  { minCount = NEXT_EXERCISE_MIN_COUNT, excludeIds = [] } = {}
) {
  const { frequency, mostRecentSessionIndex } = computeNextExerciseFrequencies(
    targetExerciseId, sessionsDayLogs, excludeIds
  );

  let bestId = null;
  let bestCount = -1;
  let bestRecency = Infinity;
  for (const [id, count] of frequency) {
    if (count < minCount) continue;
    const recency = mostRecentSessionIndex.get(id);
    if (count > bestCount || (count === bestCount && recency < bestRecency)) {
      bestId = id;
      bestCount = count;
      bestRecency = recency;
    }
  }
  return bestId === null ? null : { id: bestId, count: bestCount };
}`,
      },
      {
        title: 'Saving only completed sets',
        problem:
          'Sets are prefilled from the last session, so the form always holds plausible numbers. Saving them as they are would record workouts that never happened, and a double submit could create two rows for the same day.',
        approach:
          "Both the logging path and the history-editing path use one sanitizer, which keeps only sets marked complete with valid numbers. Logs are upserted on (user_id, exercise_id, logged_at) with a unique index behind it, so saving the same exercise twice in one day updates a single row. Prefill makes logging faster without adding fake history, and the two save paths can't drift apart.",
        file: 'lib/sets.js · actions/logs.js',
        code: `export function sanitizeSets(sets) {
  return (sets ?? [])
    .map((s) => ({
      weight: Number(s.weight),
      reps: Number(s.reps),
      completed: s.completed === true,
      isWarmup: s.isWarmup === true,
    }))
    .filter((s) =>
      s.completed &&
      Number.isFinite(s.weight) && Number.isFinite(s.reps) &&
      s.weight >= 0 && s.reps > 0
    );
}

// actions/logs.js
await supabase.from("exercise_logs").upsert(
  { user_id: user.id, exercise_id: exerciseId, logged_at: loggedAt,
    sets: cleanedSets, top_weight: topWeight, total_volume: totalVolume },
  { onConflict: "user_id,exercise_id,logged_at" }
);`,
      },
      {
        title: 'Webhooks that survive drifted customer IDs',
        problem:
          'Subscription changes reach the app only through Stripe webhooks. During testing, profiles.stripe_customer_id fell out of sync with Stripe, and renewal and cancellation events could no longer find the right user.',
        approach:
          'Each request is verified with the webhook signing secret before anything is read, and unsigned requests get a 400. At checkout the user ID is also stored in the subscription metadata. Later subscription events look the user up by that ID first and fall back to the customer ID, so upgrades, renewals, and cancellations sync without manual fixes.',
        file: 'app/api/stripe/webhook/route.js',
        code: `let event;
try {
  event = stripe.webhooks.constructEvent(
    payload, signature, process.env.STRIPE_WEBHOOK_SECRET
  );
} catch (err) {
  return NextResponse.json(
    { error: \`Webhook signature verification failed: \${err.message}\` },
    { status: 400 }
  );
}

// customer.subscription.updated / .deleted
const userId = subscription.metadata?.user_id;
const query = supabase.from("profiles").update(subscriptionFields(subscription));
const { error } = userId
  ? await query.eq("id", userId)
  : await query.eq("stripe_customer_id", subscription.customer);`,
      },
    ],
    tradeoffs: [
      'Recommendations are aggregated in JavaScript inside a server action. A Postgres function would also work, but each user\'s history is small and the JavaScript is easier to read. I would move it into SQL if query size became a problem.',
      "The webhook assumes that applying the latest subscription state is idempotent, and it doesn't record processed event IDs. If Stripe delivered events out of order, an older status could briefly win. Storing event IDs or comparing timestamps would close that gap.",
      'exercises.last_logged_at is updated in a second query after the log upsert, outside a transaction. If that query fails, auto-archiving runs late for that exercise; a Postgres function would make the two writes atomic.',
    ],
  },
  {
    slug: 'surgery-status-board',
    title: 'Surgery Status Board',
    tagline: 'Real-time patient status board built by a five-person Agile team',
    kind: 'Team project',
    role: 'Developer on a team of 5 (2 Scrum Masters, 3 developers)',
    timeline: 'Jul - Aug 2025 (6-week Chingu Voyage)',
    stack: ['nextjs', 'react', 'typescript', 'supabase', 'tailwind'],
    badges: ['NEXTJS', 'TYPESCRIPT', 'SUPABASE', 'TEAM'],
    links: {
      website: 'https://chingu-voyage-56-team-38-surgery-status-board.vercel.app/',
      github: 'https://github.com/chingu-voyages/v56-tier3-team-38',
    },
    captions: ['Waiting room display', 'Patient update screen', 'New patient entry'],
    overview: [
      'A web app that tracks surgical patients through each stage of their procedure. Staff add patients and update their status, and families follow along on a color-coded board in the waiting room that updates in real time.',
      'We built it in six weeks during Chingu Voyage 56 (Tier 3). I wrote about half of the commits in the repository: the status board, the home and login screens, status updates, and patient ID generation.',
    ],
    team: [
      'The two Scrum Masters ran the backlog in GitHub Projects, broke requirements into sprint items, and scheduled meetings.',
      'Each sprint had planning, a review, and a retrospective, and the repo has issue templates for user stories, tasks, and bugs.',
      'All changes went through feature branches and pull request review.',
    ],
    highlights: [
      'The status display: a color-coded board with real-time updates and pagination that rotates every 20 seconds for long patient lists.',
      'The status update screen, which looks up a patient by ID and moves them one step forward or back without skipping a stage.',
      "Patient intake with unique 6-character patient numbers that never contain the patient's personal data.",
      'Home, login, and navigation for the Guest, Admin, and Surgical Team roles, with write actions hidden from guests.',
      'Layouts for the home and status update screens, the home-screen copy, and consistent headers and footers.',
    ],
    decisions: [
      {
        title: 'Patient numbers with no personal data',
        problem:
          'Patients appear on screen as a short number. It had to be unique, easy to read aloud, and impossible to trace back to a name or birth date.',
        approach:
          "The code generates a random 6-character alphanumeric ID, rejects any candidate containing a token from the patient's own details, and checks the database for collisions, retrying up to 50 times. The result is a readable ID that never encodes personal data.",
        file: 'surgical-app/src/services/patientService.ts',
        language: 'typescript',
        code: `function randomId6() {
  return Array.from({ length: 6 }, () =>
    ALNUM[Math.floor(Math.random() * ALNUM.length)]
  ).join('');
}

async function generateSafePatientId(input: PatientCreate) {
  const pii = buildPiiTokens(input);
  for (let i = 0; i < 50; i++) {
    const id = randomId6();
    if ([...pii].some(t => id.toLowerCase().includes(t))) continue;
    if (!(await isPatientIdTaken(id))) return id;
  }
  throw new Error('Failed to generate safe ID');
}`,
      },
      {
        title: "A status workflow that can't skip steps",
        problem:
          'Staff update statuses under time pressure. One mis-tap could jump a patient from "Checked In" to "Complete" and mislead the family in the waiting room.',
        approach:
          'One canonical STATUS_ORDER defines the stages, and the allowed transitions come from it: the previous status or the next one. The UI only offers those moves, and the update function checks the rule again before writing.',
        file: 'surgical-app/src/services/patientService.ts',
        language: 'typescript',
        code: `export const STATUS_ORDER = [
  'Checked In', 'Pre-Procedure', 'In-Progress',
  'Closing', 'Recovery', 'Complete', 'Dismissal',
];

export function getAllowedStatuses(current: PatientStatus) {
  const i = STATUS_ORDER.indexOf(current);
  return [STATUS_ORDER[i - 1], STATUS_ORDER[i + 1]].filter(Boolean);
}

const allowed = getAllowedStatuses(patient.status);
if (!allowed.includes(nextStatus)) {
  throw new Error('Only the previous or the next status is allowed.');
}`,
      },
      {
        title: 'Live updates without polling',
        problem:
          'The waiting-room screen runs unattended all day, so it has to show changes made on other devices without anyone refreshing it.',
        approach:
          'The board subscribes to Supabase Realtime changes on the patients table. Any insert, update, or delete triggers a refetch of the active list, and the channel is removed when the component unmounts. Status changes show up on the board within seconds of being saved.',
        file: 'surgical-app/src/components/StatusTable.tsx',
        language: 'typescript',
        code: `useEffect(() => {
  const handleChange = async () => {
    const data = await getActivePatientsForBoard();
    setRows(data.map((d) => ({ id: d.id, status: d.status, name: d.name })));
    setPage(0);
  };

  const channel = supabase
    .channel('patients-changes', { config: { broadcast: { self: false } } })
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'patients' }, handleChange)
    .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'patients' }, handleChange)
    .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'patients' }, handleChange)
    .subscribe();

  return () => { supabase.removeChannel(channel); };
}, [supabase]);`,
      },
    ],
    tradeoffs: [
      'Login uses hard-coded demo accounts, and the session lives in localStorage. That was enough to demo the role-based flows within six weeks, but the roles are enforced only in the UI. A real deployment would need Supabase Auth and row-level security so the database enforces them.',
      'The one-step status rule is checked in client-side service code, so a direct API call could bypass it. A database trigger or RPC would make the rule authoritative.',
      'The public board shows patient names next to their status. A production version should show only the patient number, which is what the privacy-safe IDs were designed for.',
      "Every realtime event refetches the full active list. That's fine for one waiting room; a busier system should apply the change payload directly.",
    ],
  },
  {
    slug: 'next-level-performance-dyno',
    legacySlugs: ['next-level-complete-auto-care'],
    title: 'Next Level Performance & Dyno',
    tagline: 'Fast static site that became the #1 local result for an auto shop',
    kind: 'Client project',
    role: 'Sole developer; also ran SEO, analytics, and Google Ads',
    timeline: 'Aug 2024 - Jun 2026',
    stack: ['html', 'css', 'javascript', 'netlify'],
    badges: ['HTML', 'CSS', 'JAVASCRIPT', 'CLIENT'],
    links: {
      website: 'https://nextlevelperformancedyno.com/',
      github: 'https://github.com/bellhwi/next-level-complete-auto-care-center',
    },
    captions: [
      'Google local search result',
      'Netlify inbox showing customer inquiry emails',
      'Google PageSpeed Insights report',
    ],
    overview: [
      'A multi-page website for an auto repair and performance shop, formerly Next Level Complete Auto Care. When someone nearby searches for auto repair, the shop should show up first, and the visit should turn into a call, a booking, or a chat.',
      'I built it with plain HTML, CSS, and JavaScript to keep pages small and fast, and ran SEO and Google Ads on top of it. I kept maintaining it through a rebrand and a move to a new domain.',
    ],
    highlights: [
      'Ranked #1 in Google local results for core service keywords within three months.',
      'Brings in 40+ leads a month from Google search through calls, bookings, and forms.',
      'The hero image is preloaded with high fetch priority, below-the-fold images are lazy-loaded, and a small custom script replaced the slider library.',
      'The UTM source carries into booking links and forms, and chat messages are tracked in Google Tag Manager.',
    ],
    decisions: [
      {
        title: 'Speeding up the largest paint',
        problem:
          'The hero was an image slider, so the largest element on the page waited for a slider library to load and initialize. On mobile, that pushed Largest Contentful Paint well past what Google considers good.',
        approach:
          'I preloaded the first hero image and rendered it as a plain <img> with fetchpriority="high" and fixed dimensions, then lazy-loaded everything below the fold. Later I replaced the slider library with a small custom slider that does only what the page needs. The hero now paints without waiting for JavaScript.',
        file: 'index.html',
        language: 'markup',
        code: `<!-- Preload Hero Image -->
<link rel="preload" as="image" href="img/corvette-800.webp" fetchpriority="high" />

<img
  id="hero-lcp"
  src="img/corvette-800.webp"
  alt="corvette"
  width="800"
  height="500"
  loading="eager"
  fetchpriority="high"
/>`,
      },
      {
        title: 'Knowing which channel produced a lead',
        problem:
          'Leads came in by phone, booking link, form, and a third-party chat widget. Without attribution, there was no way to tell whether SEO or ads produced them.',
        approach:
          'The script stores utm_source for the session and appends it to every booking link and to a hidden form field. The chat widget renders inside nested Shadow DOMs and has no ready event, so the script waits for it, finds the send button, and pushes a GTM event on click. That attribution guided where the ad budget went.',
        file: 'js/script.js',
        code: `const utm_source = urlParams.get('utm_source')
if (utm_source) sessionStorage.setItem('utm_source', utm_source)
const storedUtm = sessionStorage.getItem('utm_source')

if (storedUtm) {
  document.querySelectorAll('.conversion-book-call').forEach((btn) => {
    const href = btn.getAttribute('href')
    const separator = href.includes('?') ? '&' : '?'
    btn.setAttribute('href', \`\${href}\${separator}utm_source=\${storedUtm}\`)
  })
  const utmInput = document.getElementById('utm_source')
  if (utmInput) utmInput.value = storedUtm
}

// Chat widget: two nested shadow roots, no "ready" event to hook into
const sendBtn = outerShadow
  .querySelector('widget-box')?.shadowRoot
  ?.getElementById('lc_text-widget--send-btn')
sendBtn.addEventListener('click', () => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ event: 'chat_message_sent' })
})`,
      },
      {
        title: 'Rebranding without losing search rankings',
        problem:
          "The business renamed itself and moved to a new domain. Its rankings were tied to the old URLs, and the old domain's DNS also handled email (MX, SPF, DKIM) and Search Console verification. Registrar forwarding didn't work because Netlify already managed DNS.",
        approach:
          'I kept the old domain as primary in Netlify so no DNS records had to be re-created, added the new domain as an alias, and forced a path-preserving 301 redirect in netlify.toml. Both email addresses stayed active as aliases. Every old URL now redirects permanently to the same path on the new domain, and email never went down.',
        file: 'netlify.toml',
        language: 'toml',
        code: `[[redirects]]
  from = "https://nextlevelcompleteauto.com/*"
  to = "https://nextlevelperformancedyno.com/:splat"
  status = 301
  force = true`,
      },
    ],
    tradeoffs: [
      'The chat tracking polls every 300 ms until the widget appears, because the vendor exposes no ready event. If the vendor changes its markup, the tracking breaks.',
      'utm_source is appended to links without encodeURIComponent. The values come from our own ad URLs today, but encoding them would be safer.',
      'Because the site is plain HTML, shared sections like the header and footer are copied into every page. A static site generator would remove that duplication without adding runtime JavaScript.',
    ],
    related: [
      { label: 'SEO-Safe Domain Migration for a Client', to: '/blog/seo-safe-domain-migration-for-a-client' },
      { label: 'How to Track Events Inside a Shadow DOM', to: '/blog/how-to-track-events-inside-a-shadow-dom' },
    ],
  },
  {
    slug: 'specialized-auto-tint',
    title: 'Specialized Auto Tint',
    tagline: "Bilingual Next.js site built to match a designer's PSD files",
    kind: 'Client project',
    role: 'Sole developer',
    timeline: 'Jan 2024 - Aug 2026',
    stack: ['nextjs', 'react', 'javascript', 'tailwind'],
    badges: ['NEXTJS', 'REACT', 'TAILWINDCSS', 'CLIENT'],
    links: {
      website: 'https://s-worktint.com/',
      github: 'https://github.com/bellhwi/specialized-auto-tint',
    },
    captions: [
      'Live site built from the PSD',
      'English and Korean pages',
      '"Get Quote" modal',
    ],
    overview: [
      'A website for an auto tint shop in Irvine, CA. The client, a former UI/UX designer, provided detailed PSD files and expected the live site to match them exactly, in English and Korean.',
      'I built it with the Next.js App Router so each language has its own statically generated pages that search engines can index.',
    ],
    highlights: [
      'A custom site that matches the PSD spacing and typography at every breakpoint.',
      'Separate English and Korean routes, each with translated metadata.',
      'A "Get Quote" modal that opens from any page without a reload.',
      'A sitemap and robots.txt generated at build time with next-sitemap.',
    ],
    decisions: [
      {
        title: 'Matching the PSD files',
        problem:
          'Every detail had to match the PSD files, including spacing, typography, and how layouts change on smaller screens.',
        approach:
          'I translated the PSD into React components styled with Tailwind and checked each layout against the design at every breakpoint.',
      },
      {
        title: 'Korean pages without a translation widget',
        problem:
          'The client wanted real Korean pages instead of a translation widget, and both languages needed to rank in search.',
        approach:
          'I used route-based localization in the App Router: / for English and /ko for Korean. Each route is statically generated with its own translated metadata, so search engines can index both languages.',
        file: 'src/app/ko/page.js',
        code: `import MainKo from '@/pages/MainKo'

export const metadata = {
  title: '자동차 윈도우 틴팅 어바인 | Specialized Auto Tint',
  description:
    'Specialized Auto Tint에서 제공해드리는 고품질 윈도우 틴팅 서비스로 남다른 편안함을 경험해보세요.',
}`,
      },
      {
        title: 'A quote form that stays on the page',
        problem: 'Visitors needed to request a quote without losing their place on the page.',
        approach:
          'QuoteModal is a reusable client component. It opens from a CTA, closes when the overlay is clicked, and stops clicks inside the modal from closing it. A quote request is one click away from anywhere on the site.',
        file: 'src/components/QuoteModal.jsx',
        code: `const QuoteModal = ({ ko }) => {
  const [showModal, setShowModal] = useState(false)

  const handleQuotationModal = () => setShowModal(!showModal)
  const handleOverlayClick = () => setShowModal(false)
  const handleModalContentClick = (e) => e.stopPropagation()
  // ...
}`,
      },
    ],
    tradeoffs: [
      'The Korean pages are separate components (MainKo, ProductsKo) instead of one component fed by translation files. That was the fastest way to match the design in both languages, but every content change has to be made twice.',
      "The pages don't declare hreflang alternates yet. Adding alternates.languages to the metadata would tell search engines that / and /ko are the same page in two languages.",
    ],
  },
]

export { projects }
